import { db } from '@/lib/firebase';
import { Timestamp, doc, getDoc, updateDoc, deleteField, onSnapshot } from 'firebase/firestore';
import { auth } from '@/lib/firebase';

// Helper function to safely convert Firestore Timestamp to Date
const toDate = (value: any): Date => {
    if (!value) return new Date();
    return value instanceof Date ? value : value.toDate ? value.toDate() : new Date(value);
};

/**
 * Interface representing an Objective.
 */
export interface Objective {
    id: string;
    addedBy: string;
    dateAdded: Date | Timestamp;
    dateUpdated: Date | Timestamp;
    owner: string;
    objectiveText: string;
    objectiveTargetDate: Date | Timestamp;
    updatedBy: string;
    description?: string;
    measurement?: string;
    frameworks?: string[];
    relatedRisks?: string[];
    relatedControls?: (string | number)[];
}

export interface ObjectiveCreateData {
    objectiveText: string;
    objectiveTargetDate: Date;
    owner?: string;
    description?: string;
    measurement?: string;
    frameworks?: string[];
    relatedRisks?: string[];
    relatedControls?: (string | number)[];
}

export interface ObjectiveUpdateData {
    objectiveText?: string;
    objectiveTargetDate?: Date;
    owner?: string;
    description?: string;
    measurement?: string;
    frameworks?: string[];
    relatedRisks?: string[];
    relatedControls?: (string | number)[];
}

/**
 * Fetches all objectives from Firestore.
 * @returns A promise that resolves to an array of Objective objects.
 */
export async function getAllObjectives(companyID: string): Promise<Objective[]> {
    const docPath = `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = doc(db, docPath);

    try {
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data()?.ismsObjectives as { [key: string]: any } | undefined;
            if (data && typeof data === 'object') {
                return Object.entries(data).map(([id, objective]) => ({
                    id,
                    addedBy: objective.addedBy,
                    dateAdded: toDate(objective.dateAdded),
                    dateUpdated: toDate(objective.dateUpdated),
                    owner: objective.owner,
                    objectiveText: objective.objectiveText,
                    objectiveTargetDate: toDate(objective.objectiveTargetDate),
                    updatedBy: objective.updatedBy,
                    description: objective.description || '',
                    measurement: objective.measurement || '',
                    frameworks: Array.isArray(objective.frameworks) ? objective.frameworks : [],
                    relatedRisks: Array.isArray(objective.relatedRisks) ? objective.relatedRisks : [],
                    relatedControls: Array.isArray(objective.relatedControls) ? objective.relatedControls : [],
                }));
            }
        }
        return [];
    } catch (error) {
        console.error('Error fetching objectives:', error);
        throw new Error('Failed to load objectives');
    }
}

/**
 * Adds a new objective to Firestore.
 * @param objectiveText - The text of the objective.
 * @param objectiveTargetDate - The target date for the objective.
 * @returns A promise that resolves when the objective is added.
 */
export async function addObjective(
    companyID: string,
    data: ObjectiveCreateData,
): Promise<void> {
    const docPath = `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = doc(db, docPath);

    try {
        const docSnap = await getDoc(docRef);
        const ismsObjectives = docSnap.data()?.ismsObjectives || {};

        // Generate a new ID
        const newId = generateNewId(ismsObjectives);
        const fallbackUserId = auth.currentUser?.uid || 'system';
        const ownerId = data.owner ?? fallbackUserId;
        const now = Timestamp.now();

        // Create the new objective
        const newObjective = {
            addedBy: fallbackUserId,
            dateAdded: now,
            dateUpdated: now,
            owner: ownerId,
            objectiveText: data.objectiveText,
            objectiveTargetDate: Timestamp.fromDate(new Date(data.objectiveTargetDate)),
            updatedBy: fallbackUserId,
            description: data.description || '',
            measurement: data.measurement || '',
            frameworks: Array.isArray(data.frameworks) ? data.frameworks : [],
            relatedRisks: Array.isArray(data.relatedRisks) ? data.relatedRisks : [],
            relatedControls: Array.isArray(data.relatedControls) ? data.relatedControls : [],
        };

        // Update Firestore
        await updateDoc(docRef, {
            [`ismsObjectives.${newId}`]: newObjective,
        });
    } catch (error) {
        console.error('Error adding objective:', error);
        throw new Error('Failed to add objective');
    }
}

/**
 * Updates an existing objective in Firestore.
 * @param id - The ID of the objective to update.
 * @param updates - An object containing the fields to update.
 * @returns A promise that resolves when the objective is updated.
 */
export async function updateObjective(
    companyID: string,
    id: string,
    updates: ObjectiveUpdateData
): Promise<void> {
    const docPath = `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = doc(db, docPath);

    try {
        const updateData: any = {
            dateUpdated: Timestamp.now(),
            updatedBy: auth.currentUser?.uid || 'system',
        };

        if (updates.objectiveText) {
            updateData[`ismsObjectives.${id}.objectiveText`] = updates.objectiveText;
        }

        if (updates.objectiveTargetDate) {
            updateData[`ismsObjectives.${id}.objectiveTargetDate`] =
                Timestamp.fromDate(new Date(updates.objectiveTargetDate));
        }

        if (updates.owner !== undefined) {
            updateData[`ismsObjectives.${id}.owner`] = updates.owner;
        }

        if (updates.description !== undefined) {
            updateData[`ismsObjectives.${id}.description`] = updates.description;
        }

        if (updates.measurement !== undefined) {
            updateData[`ismsObjectives.${id}.measurement`] = updates.measurement;
        }

        if (updates.frameworks !== undefined) {
            updateData[`ismsObjectives.${id}.frameworks`] = Array.isArray(updates.frameworks) ? updates.frameworks : [];
        }

        if (updates.relatedRisks !== undefined) {
            updateData[`ismsObjectives.${id}.relatedRisks`] = Array.isArray(updates.relatedRisks) ? updates.relatedRisks : [];
        }

        if (updates.relatedControls !== undefined) {
            updateData[`ismsObjectives.${id}.relatedControls`] = Array.isArray(updates.relatedControls) ? updates.relatedControls : [];
        }

        await updateDoc(docRef, updateData);
    } catch (error) {
        console.error('Error updating objective:', error);
        throw new Error('Failed to update objective');
    }
}

/**
 * Deletes an objective from Firestore.
 * @param companyID - The company ID
 * @param id - The ID of the objective to delete.
 * @returns A promise that resolves when the objective is deleted.
 */
export async function deleteObjective(companyID: string, id: string): Promise<void> {
    const docPath = `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = doc(db, docPath);

    try {
        await updateDoc(docRef, {
            [`ismsObjectives.${id}`]: deleteField(),
        });
    } catch (error) {
        console.error('Error deleting objective:', error);
        throw new Error('Failed to delete objective');
    }
}

/**
 * Sets up a real-time listener for objectives.
 * @param callback - A function that will be called with the updated array of objectives.
 * @returns A function to unsubscribe from the listener.
 */
export function onObjectivesChange(
    companyID: string,
    callback: (objectives: Objective[]) => void
): () => void {
    let unsubscribe: (() => void) | null = null;

    const docPath = `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/ismsManual`;
    const docRef = doc(db, docPath);

    unsubscribe = onSnapshot(
        docRef,
        (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data()?.ismsObjectives as { [key: string]: any } | undefined;
                if (data && typeof data === 'object') {
                    const objectives = Object.entries(data).map(([id, objective]) => ({
                        id,
                        addedBy: objective.addedBy,
                        dateAdded: toDate(objective.dateAdded),
                        dateUpdated: toDate(objective.dateUpdated),
                        owner: objective.owner,
                        objectiveText: objective.objectiveText,
                        objectiveTargetDate: toDate(objective.objectiveTargetDate),
                        updatedBy: objective.updatedBy,
                        description: objective.description || '',
                        measurement: objective.measurement || '',
                        frameworks: Array.isArray(objective.frameworks) ? objective.frameworks : [],
                        relatedRisks: Array.isArray(objective.relatedRisks) ? objective.relatedRisks : [],
                        relatedControls: Array.isArray(objective.relatedControls) ? objective.relatedControls : [],
                    }));
                    callback(objectives);
                } else {
                    callback([]);
                }
            } else {
                callback([]);
            }
        },
        (error) => {
            console.error('Error in objectives listener:', error);
        }
    );
    // Return a function to unsubscribe
    return () => {
        if (unsubscribe) {
            unsubscribe();
        }
    };
}

/**
 * Generates a new unique ID for an objective.
 * @param ismsObjectives - The current objectives object.
 * @returns A new unique ID as a string.
 */
function generateNewId(ismsObjectives: { [key: string]: any }): string {
    const existingIds = Object.keys(ismsObjectives)
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return (maxId + 1).toString();
}

export default {
    getAllObjectives,
    addObjective,
    updateObjective,
    deleteObjective,
    onObjectivesChange,
};
